// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xScreen } from "@awc/XDL/Utilities";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Search from "@insite/mobius/Icons/Search";
import Link from "@insite/mobius/Link";
import { Pagination } from "@insite/mobius/Pagination";
import TextField from "@insite/mobius/TextField";
import React from "react";
import { css, withTheme } from "styled-components";

const numberOfResults = 3000;

const generateRow = (index: any) => {
    const arg1 = Math.floor(Math.random() * 100);
    const arg2 = Math.floor(Math.random() * 2) + 1;
    return {
        invoice: `0${index}0${arg2}0${arg1}`,
        invoiceDate: arg2 === 1 ? "7/20/2020" : "8/14/2019",
        terms: arg2 * arg1 < 100 ? "Net 30" : "Net 60",
        dueDate: arg2 * arg1 < 50 ? "8/20/2020" : "Paid",
        address: arg2 * arg1 < 60 ? "Edina Warehouse 1" : "Plymouth Warehouse",
        status: arg2 * arg1 > 40 ? "open" : "overdue",
        invoiceTotal: `${arg1 * arg2}.00`,
        currentBalance: arg2 * arg1 < 50 ? `${arg1}.00` : "0.00",
    };
};

const items: any = [];
for (let i = 0; i < 10; i++) {
    items[i] = generateRow(i);
}
const FakeLink = (props: any) => <Link href="#" typographyProps={{ size: 13 }} {...props} />;

interface InvoiceData {
    invoice: any;
    invoiceDate: any;
    terms: any;
    dueDate: any;
    address: any;
    status: any;
    invoiceTotal: any;
    currentBalance: any;
}

const textFieldStyle = {
    cssOverrides: {
        formInputWrapper: css`
            width: 250px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
        formField: css`
            width: 250px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

class DummyPaginationTable extends React.Component<any, any> {
    constructor(props: any) {
        super(props);
        this.state = {
            items,
            currentPage: 1,
            resultsPerPage: 10,
        };
        this.changePage = this.changePage.bind(this);
        this.changeNumberOfResultsPerPage = this.changeNumberOfResultsPerPage.bind(this);
    }

    changePage(newPageNumber: any) {
        const { items, resultsPerPage } = this.state;
        for (let i = (newPageNumber - 1) * resultsPerPage; i < (newPageNumber + 1) * resultsPerPage; i++) {
            if (!items[i] && i < numberOfResults) {
                items[i] = generateRow(i);
            }
        }
        this.setState({ items, currentPage: newPageNumber });
    }

    changeNumberOfResultsPerPage(event: any) {
        // eslint-disable-next-line radix
        const resultsPerPage = parseInt(event.target.value);
        const finalPageIndex = Math.floor((numberOfResults - 1) / resultsPerPage);
        let currentPage = this.state.currentPage;
        if (currentPage > finalPageIndex) {
            currentPage = finalPageIndex;
        }
        this.setState({ resultsPerPage }, () => this.changePage(currentPage));
    }

    render() {
        const { items, currentPage, resultsPerPage } = this.state;
        const data: InvoiceData[] = items.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage);
        const paginationComponent = (
            <Pagination
                resultsPerPage={resultsPerPage}
                resultsCount={numberOfResults}
                currentPage={currentPage}
                resultsPerPageOptions={[5, 10, 50, 100]}
                onChangeResultsPerPage={this.changeNumberOfResultsPerPage}
                onChangePage={this.changePage}
                theme={this.props.theme}
            />
        );
        return (
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                {/* note: in the interest of a slightly more concise example, the implementation of the below is not implemented responsively */}
                <div
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}
                >
                    <TextField placeholder="search" iconProps={{ src: Search }} {...textFieldStyle} />
                    {paginationComponent}
                </div>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader tight title="Invoice Number">
                            Invoice #
                        </DataTableHeader>
                        <DataTableHeader tight>Invoice Date</DataTableHeader>
                        <DataTableHeader tight>Terms</DataTableHeader>
                        <DataTableHeader tight>Due Date</DataTableHeader>
                        <DataTableHeader>Ship To / Pick Up</DataTableHeader>
                        <DataTableHeader tight>Status</DataTableHeader>
                        <DataTableHeader tight alignX="right">
                            Invoice Total
                        </DataTableHeader>
                        <DataTableHeader tight alignX="right">
                            Current Balance
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {data.map(
                            ({
                                invoice,
                                invoiceDate,
                                terms,
                                dueDate,
                                address,
                                status,
                                invoiceTotal,
                                currentBalance,
                            }) => (
                                <DataTableRow key={invoice}>
                                    <DataTableCell>
                                        <FakeLink>{invoice}</FakeLink>
                                    </DataTableCell>
                                    <DataTableCell>{invoiceDate}</DataTableCell>
                                    <DataTableCell>{terms}</DataTableCell>
                                    <DataTableCell>{dueDate}</DataTableCell>
                                    <DataTableCell>{address}</DataTableCell>
                                    <DataTableCell>{status}</DataTableCell>
                                    <DataTableCell alignX="right">${invoiceTotal}</DataTableCell>
                                    <DataTableCell alignX="right">${currentBalance}</DataTableCell>
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
                {paginationComponent}
            </div>
        );
    }
}

export default withTheme(DummyPaginationTable);
